import React, { useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import useUiuxStore from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';
import { ShowSuccessFetch, showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import {
    downloadExcel_HRM1040_R01Education, downloadExcel_HRM1040_R01Family,
    getHRM1040_R01AllCount, getHRM1040_R01EducationList, getHRM1040_R01FamilyList,
    getHRM1040_R01List, patchHRM1040_R01
} from './Api_HRM1040_R01';
import { HRM1040_R01EducationDto, HRM1040_R01FamilyDto, HRM1040_R01HeaderDto, HRM1040_R01HeaderGridRow } from './Dto_HRM1040_R01';
import { useHRM1040_R01Store } from './Store_HRM1040_R01';

/** 세 그리드 참조와 화면 메시지 변경 함수이다. */
interface Props {
    gridRef: React.RefObject<AgGridReact<HRM1040_R01HeaderGridRow> | null>; // 사원 그리드
    familyGridRef: React.RefObject<AgGridReact<HRM1040_R01FamilyDto> | null>; // 가족 그리드
    educationGridRef: React.RefObject<AgGridReact<HRM1040_R01EducationDto> | null>; // 학력 그리드
    setMessage: (value: string) => void; // 하단 메시지 설정
}

/**
 * 가족·학력 화면의 조회, 행 선택, 저장, 삭제, Excel 동작을 연결한다. 행 추가는 각 그리드가 직접 처리한다.
 * @param gridRef 사원 그리드 참조
 * @param familyGridRef 가족 그리드 참조. 저장 전 편집 확정에 쓴다.
 * @param educationGridRef 학력 그리드 참조. 저장 전 편집 확정에 쓴다.
 * @param setMessage 조회·저장 결과를 표시하는 하단 메시지 변경 함수
 * @returns Container가 액션바·그리드에 연결할 동작 함수
 */
export const HRM1040_R01Content = ({ gridRef, familyGridRef, educationGridRef, setMessage }: Props) => {
    // 공통 확인창(ConfirmModal)의 내용과 표시 여부를 바꾸는 함수를 가져온다.
    const { setConfirmModalProps, setConfirmModalVisible } = useUiuxStore();
    // 조회·저장 중 로딩 스피너 표시 함수를 가져온다.
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    // 현재 사용자의 이 화면 액션바 버튼 권한을 읽어 온다.
    useFetchActionbarAuth('HRM1040_R01');

    /**
     * 사원 한 명의 가족·학력 목록을 함께 조회한다.
     * @param empNo 조회할 사원번호. 없으면 두 목록을 비운다.
     * @returns 조회 완료를 나타내는 Promise<void>
     */
    const loadDetails = useCallback(async (empNo?: string) => {
        const state = useHRM1040_R01Store.getState();
        /**
         * 가족·학력 목록과 비교용 원본 목록을 모두 비운다.
         * 조회 실패 시 이전 사원의 목록이 새 사원 화면에 남아 새 사원번호로 저장되는 것을 막는다.
         */
        const clearDetails = () => {
            state.setHRM1040_R01FamilyList([]);
            state.setOriginalHRM1040_R01FamilyList([]);
            state.setHRM1040_R01EducationList([]);
            state.setOriginalHRM1040_R01EducationList([]);
        };
        if (!empNo) {
            clearDetails();
            return;
        }
        try {
            const [family, education] = await Promise.all([
                getHRM1040_R01FamilyList(empNo),
                getHRM1040_R01EducationList(empNo),
            ]);
            // 둘 중 하나라도 실패하면 한쪽만 표시하지 않고 두 목록을 모두 비운다.
            if (!family.data.successFlag || !education.data.successFlag) {
                clearDetails();
                return;
            }
            state.setHRM1040_R01FamilyList(family.data.data);
            state.setOriginalHRM1040_R01FamilyList(family.data.data);
            state.setHRM1040_R01EducationList(education.data.data);
            state.setOriginalHRM1040_R01EducationList(education.data.data);
        } catch {
            // 네트워크 오류처럼 요청 자체가 예외로 끝난 경우에도 목록을 비운다. 오류 알림은 공통 인터셉터가 표시한다.
            clearDetails();
        }
    }, []);

    /**
     * 검색 조건으로 사원과 건수를 조회하고 직전 사원 또는 첫 사원을 선택한다.
     * @param option search(새 조회, 스크롤 초기화) 또는 refetch(저장 후 재조회)
     * @param empNo 조회 후 선택할 사원번호. 없으면 직전 선택 사원을 다시 선택한다.
     * @param quiet true이면 조회 완료·빈 목록 알림을 표시하지 않는다.
     * @returns 조회 완료를 나타내는 Promise<void>
     */
    const executeDataFetch = useCallback(async (option: 'search' | 'refetch', empNo?: string, quiet = false) => {
        setLoadingSpinner(true);
        try {
            const state = useHRM1040_R01Store.getState();
            // 재조회(저장 후)는 마지막으로 조회한 조건을, 새 조회는 현재 입력 중인 조건을 쓴다.
            // 조건만 바꾸고 조회하지 않은 채 저장해도 사원 목록이 바뀌지 않게 하기 위함이다.
            const filter = option === 'refetch' ? state.HRM1040_R01PastFilter : state.HRM1040_R01Filter;
            // 빈 검색 조건은 요청 파라미터에서 제외한다.
            const params = {
                compCd: filter.compCd || undefined,
                orgCd: filter.orgCd || undefined,
                empSts: filter.empSts || undefined,
                keyword: filter.keyword || undefined,
            };
            const previousEmpNo = empNo || state.selectedHRM1040_R01?.empNo;
            if (option === 'search') state.setScrollTop(null);
            const [rows, count] = await Promise.all([
                getHRM1040_R01List(option, params),
                getHRM1040_R01AllCount(params),
            ]);
            if (!rows.data.successFlag) return;
            state.setHRM1040_R01List(rows.data.data);
            // 목록 교체 후 valueIndex가 채워진 최신 목록에서 선택 사원을 찾는다.
            const current = useHRM1040_R01Store.getState();
            const selected = current.HRM1040_R01List.find(row => row.empNo === previousEmpNo)
                || current.HRM1040_R01List[0];
            current.setSelectedHRM1040_R01(selected);
            // 건수 조회가 실패하면 받은 목록 길이를 사용한다.
            const total = count.data.successFlag ? count.data.data : rows.data.data.length;
            current.setAllCount(total);
            current.setHRM1040_R01PastFilter(filter);
            await loadDetails(selected?.empNo);
            if (!quiet) {
                if (rows.data.data.length) {
                    ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${rows.data.data.length}/${total}건)`);
                } else {
                    showWarning('데이터가 없습니다.');
                }
            }
        } finally {
            setLoadingSpinner(false);
        }
    }, [loadDetails, setLoadingSpinner, setMessage]);

    /**
     * 저장하지 않은 가족·학력 변경 여부를 확인한다. 이름이 빈 신규 행은 변경으로 보지 않는다.
     * @returns 삭제 표시, 수정 행, 이름이 입력된 신규 행 중 하나라도 있으면 true
     */
    const hasChanges = useCallback((): boolean => {
        const state = useHRM1040_R01Store.getState();
        return state.deletedHRM1040_R01FamilyList.length > 0
            || state.deletedHRM1040_R01EducationList.length > 0
            || state.HRM1040_R01FamilyList.some(row => row.isModified || (row.inserted && !!row.familyNm.trim()))
            || state.HRM1040_R01EducationList.some(row => row.isModified || (row.inserted && !!row.schoolNm.trim()));
    }, []);

    /**
     * 변경이 있으면 공통 확인창으로 버릴지 묻고 다음 동작을 실행한다. 변경이 없으면 바로 실행한다.
     * @param onConfirm 확인했을 때(또는 변경이 없을 때) 실행할 동작
     */
    const confirmDiscard = useCallback((onConfirm: () => void) => {
        if (!hasChanges()) {
            onConfirm();
            return;
        }
        const close = () => {
            setConfirmModalVisible(false);
            setConfirmModalProps(undefined);
        };
        setConfirmModalProps({
            title: '변경 내용 확인',
            message: '저장하지 않은 변경 내용이 있습니다.\n변경 내용을 버리고 진행하시겠습니까?',
            confirmText: '확인',
            cancelText: '취소',
            onConfirm: () => {
                close();
                onConfirm();
            },
            onCancel: close,
        });
        setConfirmModalVisible(true);
    }, [hasChanges, setConfirmModalProps, setConfirmModalVisible]);

    /**
     * 액션바 조회와 검색 Enter에서 실행한다. 변경이 있으면 확인창을 먼저 띄운다.
     */
    const handleFetchClick = useCallback(() => {
        confirmDiscard(() => {
            void executeDataFetch('search');
        });
    }, [confirmDiscard, executeDataFetch]);

    /**
     * 사원 행을 바꾸면서 새 사원의 가족·학력을 읽는다. 같은 사원을 다시 누르면 무시한다.
     * @param row 클릭한 사원 행
     */
    const handleHeaderRowClick = useCallback((row: HRM1040_R01HeaderDto) => {
        if (row.empNo === useHRM1040_R01Store.getState().selectedHRM1040_R01?.empNo) return;
        confirmDiscard(() => {
            useHRM1040_R01Store.getState().setSelectedHRM1040_R01(row);
            void loadDetails(row.empNo);
        });
    }, [confirmDiscard, loadDetails]);

    /**
     * 마지막 작업 그리드에서 선택한 신규 행은 제거하고 기존 행은 삭제 표시한다.
     * 삭제 표시가 이미 있으면 삭제를 다시 눌렀을 때 표시를 모두 복구한다.
     */
    const handleDeleteClick = useCallback(() => {
        const state = useHRM1040_R01Store.getState();
        if (state.activeGridType === 'family') {
            if (state.deletedHRM1040_R01FamilyList.length) {
                state.setDeletedHRM1040_R01FamilyList([]);
                state.setSelectedHRM1040_R01FamilyList([]);
                return;
            }
            const selected = state.selectedHRM1040_R01FamilyList;
            state.HRM1040_R01FamilyList
                .filter(row => selected.includes(String(row.valueIndex)) && row.inserted)
                .forEach(row => state.removeHRM1040_R01FamilyList(row.valueIndex));
            state.setDeletedHRM1040_R01FamilyList(selected.filter(id =>
                state.HRM1040_R01FamilyList.some(row => String(row.valueIndex) === id && !row.inserted),
            ));
            state.setSelectedHRM1040_R01FamilyList([]);
        } else {
            if (state.deletedHRM1040_R01EducationList.length) {
                state.setDeletedHRM1040_R01EducationList([]);
                state.setSelectedHRM1040_R01EducationList([]);
                return;
            }
            const selected = state.selectedHRM1040_R01EducationList;
            state.HRM1040_R01EducationList
                .filter(row => selected.includes(String(row.valueIndex)) && row.inserted)
                .forEach(row => state.removeHRM1040_R01EducationList(row.valueIndex));
            state.setDeletedHRM1040_R01EducationList(selected.filter(id =>
                state.HRM1040_R01EducationList.some(row => String(row.valueIndex) === id && !row.inserted),
            ));
            state.setSelectedHRM1040_R01EducationList([]);
        }
    }, []);

    /**
     * 가족·학력 필수값을 확인한 뒤 두 목록을 한 요청으로 저장한다.
     * 삭제 표시 행과 이름이 빈 신규 행은 보내지 않는다. 서버는 받은 목록을 저장 후 남길 전체로 본다.
     * @returns 저장과 재조회 완료를 나타내는 Promise<void>
     */
    const handleSaveClick = useCallback(async () => {
        gridRef.current?.api.stopEditing();
        familyGridRef.current?.api.stopEditing();
        educationGridRef.current?.api.stopEditing();
        const state = useHRM1040_R01Store.getState();
        const empNo = state.selectedHRM1040_R01?.empNo;
        if (!empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }
        const family = state.HRM1040_R01FamilyList.filter(row =>
            !state.deletedHRM1040_R01FamilyList.includes(String(row.valueIndex))
            && !(row.inserted && !row.familyNm.trim()),
        );
        const education = state.HRM1040_R01EducationList.filter(row =>
            !state.deletedHRM1040_R01EducationList.includes(String(row.valueIndex))
            && !(row.inserted && !row.schoolNm.trim()),
        );
        if (!hasChanges()) {
            showWarning('저장할 내용이 없습니다.');
            return;
        }
        for (const row of family) {
            if (!row.familyNm.trim()) {
                showWarning('성명을 입력해주세요.');
                return;
            }
            // 길이 제한은 DB 컬럼 길이(성명 50자, 비고 1000자)에 맞춘다.
            if (row.familyNm.length > 50 || row.remark.length > 1000) {
                showWarning(`가족 성명 또는 비고의 길이가 초과되었습니다. > ${row.familyNm}`);
                return;
            }
            if (row.resnoBirth && !/^\d{1,6}$/.test(row.resnoBirth)) {
                showWarning(`주민번호 앞자리는 숫자 6자리 이하여야 합니다. > ${row.familyNm}`);
                return;
            }
            if (row.resnoLastkey && !/^\d/.test(row.resnoLastkey)) {
                showWarning(`주민번호 뒷자리 첫 자리는 숫자여야 합니다. > ${row.familyNm}`);
                return;
            }
        }
        for (const row of education) {
            if (!row.schoolNm.trim()) {
                showWarning('학교명을 입력해주세요.');
                return;
            }
            if (row.schoolNm.length > 50 || row.majorNm.length > 50
                || row.location.length > 50 || row.remark.length > 1000) {
                showWarning(`학력 항목의 길이가 초과되었습니다. > ${row.schoolNm}`);
                return;
            }
        }
        setLoadingSpinner(true);
        try {
            // 신규 행은 순번을 null로 보내 서버가 사원별 순번을 새로 매기게 한다.
            const response = await patchHRM1040_R01({
                empNo,
                hrmFamilyDtoList: family.map(row => ({ ...row, familySeq: row.inserted ? null : row.familySeq })),
                hrmEducationDtoList: education.map(row => ({ ...row, eduSeq: row.inserted ? null : row.eduSeq })),
            });
            if (!response.data.successFlag) return;
            showSuccess('가족·학력 저장이 완료되었습니다.');
            setMessage('가족·학력 저장이 완료되었습니다.');
            await executeDataFetch('refetch', empNo, true);
        } finally {
            setLoadingSpinner(false);
        }
    }, [gridRef, familyGridRef, educationGridRef, hasChanges, executeDataFetch, setLoadingSpinner, setMessage]);

    /**
     * 마지막으로 작업한 가족 또는 학력 그리드의 서버 Excel을 내려받는다.
     * @returns 다운로드 완료를 나타내는 Promise<void>
     */
    const handleExcelClick = useCallback(async () => {
        const state = useHRM1040_R01Store.getState();
        const empNo = state.selectedHRM1040_R01?.empNo;
        if (!empNo) {
            showWarning('사원을 먼저 선택하세요.');
            return;
        }
        const isFamily = state.activeGridType === 'family';
        const response = isFamily
            ? await downloadExcel_HRM1040_R01Family(empNo)
            : await downloadExcel_HRM1040_R01Education(empNo);
        if (!response.data.successFlag) return;
        downloadSettingExcel({
            responseData: response.data.data,
            fileName: `${isFamily ? '가족관계' : '학력정보'}_${empNo}-excel.xlsx`
        });
    }, []);

    /**
     * 화면 조건·목록·메시지를 초기화한다. 변경이 있으면 확인창을 먼저 띄운다.
     */
    const initContainerData = useCallback(() => {
        confirmDiscard(() => {
            useHRM1040_R01Store.getState().initData();
            setMessage('');
        });
    }, [confirmDiscard, setMessage]);
    return {
        fetchData: handleFetchClick,
        handleHeaderRowClick,
        handleDeleteClick,
        handleSaveClick,
        handleExcelClick,
        initContainerData,
    };
};
